import {
  h,
  initializeWidgetConfiguration,
  SgWidget,
  WithGlobalCss,
  WidgetConfigurationProvider,
} from "@sgwt-widget/core";
import { get, isEqual, map } from "lodash";
import { emit, props } from "skatejs";
import { Main } from "./container/Main";
import { FilterKeyMapper, FilterValueMapper, IO } from "./types";
import { UserSelection } from "../common/picker/types";
import { ClientDto, ClientOutput } from "../common/types/account";
import { checkFilter, checkState } from "../common/picker/utils";
import WidgetTracker from "common/component/WidgetTracker/WidgetTracker";
import { getSoaUrl, Env } from "common/api/apiUrls";

export class IcAccountsPicker extends SgWidget<
  IO<ClientDto> & { sgConnectEnable: boolean }
> {
  public static is = "ic-accounts-picker";
  public get userSelection(): UserSelection {
    return { selectedIds: this.cacheIds, selectedSearchBy: this.cacheSearchBy };
  }

  public set userSelection(selection: UserSelection) {
    if (
      !isEqual(
        { selectedIds: this.cacheIds, selectedSearchBy: this.cacheSearchBy },
        selection
      )
    ) {
      const selectedIds = get(selection, "selectedIds", []);
      const searchBy = get(selection, "selectedSearchBy", "");

      this.cacheIds = selectedIds;
      this.cacheSearchBy = searchBy;

      this.setAttribute("user-selection", JSON.stringify(selection || {}));
      this.setAttribute("selected-ids", JSON.stringify(selectedIds));
      this.setAttribute("selected-search-by", searchBy || "");
    }
  }

  public static props = {
    capacity: props.number,
    displayInline: props.boolean,
    filter: props.object,
    headers: props.object,
    maxItemNameLength: props.number,
    placeholder: props.string,
    readonlyIds: props.array,
    selectedIds: props.array,
    selectedSearchBy: props.string,
    soaUrl: props.string,
    state: props.string,
    userEmail: props.string,
    ssoV2: props.boolean,
    emitOnMount: props.boolean,
    // Temporary until https://sgithub.fr.world.socgen/DTL/sg-dashboard/issues/985 is fixed.
    sgConnectEnable: props.boolean,
  };
  private cacheIds: string[] | undefined;
  private cacheSearchBy: string | undefined;

  public render(ioProps: IO<ClientDto> & { sgConnectEnable: boolean }) {
    checkFilter(ioProps.filter, FilterKeyMapper, FilterValueMapper);
    checkState(ioProps.state);
    const userSelection = JSON.parse(
      this.getAttribute("user-selection") || "{}"
    );

    return (
      <WidgetConfigurationProvider
        widgetConfiguration={this.widgetConfiguration}
      >
        <WithGlobalCss>
          <Main
            emitOnMount={ioProps.emitOnMount}
            bus={this.widgetConfiguration.bus}
            maxItemNameLength={ioProps.maxItemNameLength}
            displayInline={ioProps.displayInline}
            capacity={ioProps.capacity}
            fetch={this.widgetConfiguration.fetch}
            filter={ioProps.filter}
            headers={ioProps.headers}
            onChange={(accounts: ClientDto[]) => {
              this.cacheIds = map<ClientDto, string | undefined>(
                accounts,
                item =>
                  this.cacheSearchBy !== "commercial"
                    ? item.id
                    : get(item, "bdrId.bdrCommercialId.id", "")
              ).filter(id => !!id) as string[];
              emit(this, "ic-accounts-picker--accounts-changed", {
                detail: {
                  accounts: map<ClientDto, ClientOutput>(accounts, item => ({
                    bdrCommercialId: get(item, "bdrId.bdrCommercialId.id", ""),
                    bdrCommercialLevel: get(
                      item,
                      "bdrId.bdrCommercialId.level",
                      ""
                    ),
                    bdrLegalId: get(item, "bdrId.bdrLegalId.id", ""),
                    bdrLegalLevel: get(item, "bdrId.bdrLegalId.level", ""),
                    icId: item.id || "",
                    name: item.name,
                  })),
                },
              });
            }}
            placeholder={ioProps.placeholder}
            readonlyIds={ioProps.readonlyIds}
            selectedIds={
              userSelection.selectedIds
                ? userSelection.selectedIds
                : ioProps.selectedIds
            }
            selectedSearchBy={
              userSelection.selectedSearchBy
                ? userSelection.selectedSearchBy
                : ioProps.selectedSearchBy
            }
            displaySearchBy={ioProps.displaySearchBy}
            soaUrl={
              ioProps.soaUrl ||
              getSoaUrl(this.widgetConfiguration.environment as Env)
            }
            ssoV2={ioProps.ssoV2 || ioProps.sgConnectEnable}
            state={ioProps.state}
            userEmail={ioProps.userEmail}
            env={this.widgetConfiguration.environment as Env}
          />
          <WidgetTracker
            widgetName={IcAccountsPicker.is}
            env={this.widgetConfiguration.environment}
          />
        </WithGlobalCss>
      </WidgetConfigurationProvider>
    );
  }
}

initializeWidgetConfiguration(IcAccountsPicker, {
  disablejQuerySanityCheck: true,
});
customElements.define(IcAccountsPicker.is, IcAccountsPicker);
